import type {
  FeatureFlag,
  Rollout,
  Targeting,
  TargetingRule,
  WeightedVariant,
} from "../../../api/platform.service.js";
import { SLOT_ATTRIBUTE } from "../FeatureFlags/targeting.js";

export const SLOT_FLAG_SCOPE = "legal500";

export const SLOT_AWARE_FLAG_KEYS: readonly string[] = ["firm-logos-by-country"];

type EvaluationContext = Record<string, string>;

type Certainty = "always" | "possibly";

function ruleMatches(rule: TargetingRule, context: EvaluationContext): boolean {
  if (!Array.isArray(rule.values)) return false;
  const actual = Object.hasOwn(context, rule.attribute) ? context[rule.attribute] : undefined;
  if (rule.operator === "in") {
    return actual !== undefined && rule.values.includes(actual);
  }
  if (rule.operator === "not_in") {
    return actual === undefined || !rule.values.includes(actual);
  }
  return false;
}

function evaluateTargeting(
  targeting: Targeting | null | undefined,
  context: EvaluationContext,
  variantKeys: readonly string[],
): string | undefined {
  if (!targeting || typeof targeting !== "object" || !Array.isArray(targeting.rules)) {
    return undefined;
  }
  if (targeting.rules.length === 0) return undefined;

  for (const rule of targeting.rules) {
    if (!rule || typeof rule !== "object") continue;
    if (!variantKeys.includes(rule.variantKey)) continue;
    if (ruleMatches(rule, context)) return rule.variantKey;
  }

  const fallback = targeting.fallbackVariantKey;
  if (typeof fallback === "string" && variantKeys.includes(fallback)) return fallback;

  return undefined;
}

function highestWeightVariantKey(weightedVariants: Rollout["weightedVariants"]): string {
  let chosen = weightedVariants[0];
  for (const weightedVariant of weightedVariants) {
    if (chosen === undefined || weightedVariant.weight > chosen.weight) {
      chosen = weightedVariant;
    }
  }
  return chosen?.variantKey ?? "";
}

function isOnVariant(flag: FeatureFlag, variantKey: string | undefined): boolean {
  return flag.variants.find(variant => variant.key === variantKey)?.value === true;
}

function isOnRolloutVariant(flag: FeatureFlag, variantKey: string): boolean {
  const declared = flag.variants.some(variant => variant.key === variantKey);
  return isOnVariant(flag, declared ? variantKey : flag.variants[0]?.key);
}

function rolloutServesOn(
  flag: FeatureFlag,
  rollout: Rollout,
  context: EvaluationContext,
  certainty: Certainty,
): boolean {
  if (!context[rollout.stickiness]) {
    return isOnRolloutVariant(flag, highestWeightVariantKey(rollout.weightedVariants));
  }
  const weighted = rollout.weightedVariants.filter(variant => variant.weight > 0);
  const isOn = (variant: WeightedVariant) => isOnRolloutVariant(flag, variant.variantKey);
  if (certainty === "always") return weighted.length > 0 && weighted.every(isOn);
  return weighted.some(isOn);
}

function servesOn(flag: FeatureFlag, context: EvaluationContext, certainty: Certainty): boolean {
  if (flag.status !== "active" || !flag.effective || !flag.defaults) return false;
  if (!flag.effective.enabled) return isOnVariant(flag, flag.defaultVariantKey ?? undefined);

  const targetedVariantKey = evaluateTargeting(
    flag.effective.targeting,
    context,
    flag.variants.map(variant => variant.key),
  );
  if (targetedVariantKey !== undefined) return isOnVariant(flag, targetedVariantKey);

  const rollout = flag.effective.rollout;
  if (!rollout) return isOnVariant(flag, flag.variants[0]?.key);
  return rolloutServesOn(flag, rollout, context, certainty);
}

function ruleValues(flag: FeatureFlag): string[] {
  const rules = flag.effective?.targeting?.rules;
  if (!Array.isArray(rules)) return [];
  return rules.flatMap(rule =>
    rule && typeof rule === "object" && Array.isArray(rule.values) ? rule.values : [],
  );
}

function slotNameNamedByNoRule(flags: FeatureFlag[]): string {
  const named = new Set(flags.flatMap(ruleValues));
  let candidate = "unnamed-slot";
  while (named.has(candidate)) candidate = `${candidate}-x`;
  return candidate;
}

export function flagKeysTargetingSlot(flags: FeatureFlag[], slot: string): string[] {
  const otherSlots = new Set([slotNameNamedByNoRule(flags), ...flags.flatMap(ruleValues)]);
  otherSlots.delete(slot);
  return flags
    .filter(
      flag =>
        SLOT_AWARE_FLAG_KEYS.includes(flag.key) &&
        flag.scopes.includes(SLOT_FLAG_SCOPE) &&
        flag.effective?.enabled === true &&
        servesOn(flag, { [SLOT_ATTRIBUTE]: slot }, "always") &&
        !servesOn(flag, {}, "possibly") &&
        [...otherSlots].every(
          otherSlot => !servesOn(flag, { [SLOT_ATTRIBUTE]: otherSlot }, "possibly"),
        ),
    )
    .map(flag => flag.key);
}
